import { graphql } from 'gatsby'
import { ThemeConsumer } from 'styled-components'
import { useQueryParam, ModalStateParam } from 'use-query-params'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React, { useState, useEffect } from 'react'
import Button from '../components/Button'
import TrialDownloadButton from '../components/TrialDownloadButton'
import Space from '@rebass/space'
import BuyLicenseForm from '../components/BuyLicenseForm'
import Card from '../components/Card'
import CompanyLogos from '../components/CompanyLogos'
import Container from '../components/Container'
import { Flex, Box } from '@rebass/grid'
import GradientWrapper from '../components/GradientWrapper'
import SEO from '../components/SEO'

import PaddleLogo from '../images/vector/paddle.svg'
import SetappLogoBanner from '../components/logos/SetappLogoBanner'
import IconChevron from '../images/vector/icon-chevron.svg'
import { getCurrencyInfo, getCurrencyInfoSync } from '../utils/currency'

const BuyNow = ({ data }) => {
  const [modalState, setModalState] = useQueryParam(
    'thank-you',
    ModalStateParam,
  )
  const [setappPrice, setSetappPrice] = useState('$12')
  const [setappPriceLoaded, setSetappPriceLoaded] = useState(false)

  // Update Setapp pricing based on location
  useEffect(() => {
    const updateSetappPrice = async () => {
      try {
        const currencyInfo = await getCurrencyInfo()
        setSetappPrice(`${currencyInfo.symbol}12`)
        setSetappPriceLoaded(true)
      } catch (error) {
        console.log('Failed to update Setapp currency:', error)
        // Keep using the sync fallback
        setSetappPriceLoaded(true)
      }
    }

    updateSetappPrice()
  }, [])

  function analyticsDownloadTrial() {
    setModalState('true')
  }

  return (
    <ThemeConsumer>
      {(theme) => (
        <>
          <SEO
            title="IconJar Pricing • Affordable Icon Management for macOS"
            description="Get IconJar for macOS with flexible pricing options. Choose annual subscription or one-time purchase. Free 14-day trial available. Secure checkout with Paddle."
            meta={[
              {
                name: 'keywords',
                content:
                  'IconJar pricing, icon management software, macOS app pricing, design tools, icon organizer, annual subscription, lifetime license, free trial',
              },
              {
                property: 'og:url',
                content: 'https://geticonjar.com/pricing',
              },
              {
                name: 'twitter:site',
                content: '@geticonjar',
              },
            ]}
          />

          {/* Structured Data */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Product',
                name: 'IconJar',
                alternateName: 'IconJar for macOS',
                description:
                  'Professional icon management and organization tool for macOS designers and developers. Organize, search, and manage your icon library with smart sets, drag-and-drop functionality, and export capabilities.',
                brand: {
                  '@type': 'Brand',
                  name: 'IconJar',
                  url: 'https://geticonjar.com',
                },
                category: 'Software Application',
                applicationCategory: 'DesignApplication',
                operatingSystem: 'macOS',
                softwareVersion: '2.11.3',
                releaseNotes:
                  'Latest version with improved performance and new features',
                featureList: [
                  'Icon organization and management',
                  'Smart sets and collections',
                  'Drag and drop functionality',
                  'SVG optimization',
                  'Export capabilities',
                  'Search and filtering',
                  'Recently used icons',
                  'Starred favorites',
                ],
                systemRequirements: {
                  '@type': 'SystemRequirements',
                  operatingSystem: 'macOS 10.15 or later',
                  processorRequirements: 'Intel or Apple Silicon',
                  memoryRequirements: '4GB RAM minimum',
                  storageRequirements: '100MB available space',
                },
                offers: [
                  {
                    '@type': 'Offer',
                    name: 'Annual Subscription',
                    description:
                      'IconJar annual subscription with regular updates and new features',
                    priceCurrency: 'USD',
                    price: '29.99',
                    priceValidUntil: '2025-12-31',
                    availability: 'https://schema.org/InStock',
                    billingIncrement: 'P1Y',
                    paymentAccepted: 'Credit Card, PayPal',
                    seller: {
                      '@type': 'Organization',
                      name: 'IconJar',
                      url: 'https://geticonjar.com',
                    },
                    url: 'https://geticonjar.com/pricing',
                    eligibleQuantity: {
                      '@type': 'QuantitativeValue',
                      minValue: 1,
                      maxValue: 10,
                    },
                  },
                  {
                    '@type': 'Offer',
                    name: 'Lifetime License',
                    description:
                      'One-time purchase for IconJar with major updates available as paid upgrades',
                    priceCurrency: 'USD',
                    price: '49.99',
                    priceValidUntil: '2025-12-31',
                    availability: 'https://schema.org/InStock',
                    paymentAccepted: 'Credit Card, PayPal',
                    seller: {
                      '@type': 'Organization',
                      name: 'IconJar',
                      url: 'https://geticonjar.com',
                    },
                    url: 'https://geticonjar.com/pricing',
                    eligibleQuantity: {
                      '@type': 'QuantitativeValue',
                      minValue: 1,
                      maxValue: 10,
                    },
                  },
                ],
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: '4.8',
                  reviewCount: '500',
                  bestRating: '5',
                  worstRating: '1',
                },
                url: 'https://geticonjar.com',
                image: 'https://geticonjar.com/og-image.jpg',
                screenshot: 'https://geticonjar.com/hero@2x.png',
                downloadUrl: 'https://geticonjar.com/download',
                softwareHelp: 'https://geticonjar.com/support',
                license: 'Commercial License',
                copyrightYear: '2024',
                creator: {
                  '@type': 'Person',
                  name: 'Davey Heuser',
                  url: 'https://twitter.com/daveyheuser',
                },
                publisher: {
                  '@type': 'Organization',
                  name: 'IconJar',
                  url: 'https://geticonjar.com',
                },
                keywords: [
                  'icon management',
                  'macOS app',
                  'design tools',
                  'icon organizer',
                  'SVG icons',
                  'design workflow',
                  'icon library',
                  'graphic design',
                ],
                audience: {
                  '@type': 'Audience',
                  audienceType: 'Designers, Developers, Creative Professionals',
                },
                isAccessibleForFree: false,
                hasOfferCatalog: {
                  '@type': 'OfferCatalog',
                  name: 'IconJar Pricing Plans',
                  itemListElement: [
                    {
                      '@type': 'Offer',
                      itemOffered: {
                        '@type': 'Product',
                        name: 'Annual Subscription',
                      },
                    },
                    {
                      '@type': 'Offer',
                      itemOffered: {
                        '@type': 'Product',
                        name: 'Lifetime License',
                      },
                    },
                  ],
                },
              }),
            }}
          />

          {/* Hero */}
          <Container my={[4, 5]}>
            <Box textAlign={'center'}>
              <h1>Pricing</h1>
              <p className="subtitle">
                Choose the plan that works best for you or start with a free
                14-day trial.
              </p>
            </Box>
          </Container>

          {/* Pricing options */}
          <Container mb={[5, 6]}>
            <Flex
              flexWrap="wrap"
              alignItems="center"
              justifyContent="center"
              mb={4}>
              <Box width={[1, 1 / 2, 7 / 16]} px={[0, 3, 4]} mb={[3, 0]}>
                <Card as={Box}>
                  <BuyLicenseForm />
                </Card>
              </Box>
              <Box width={[1, 1 / 2, 2 / 5]} px={[0, 3, 4]}>
                <Box mb={4}>
                  <h4>
                    Securely checkout with{' '}
                    <a href="https://paddle.com">
                      <PaddleLogo
                        width="68"
                        style={{
                          fill:
                            theme.appearance === 'light' ? '#31383C' : '#FFF',
                        }}
                      />
                    </a>
                  </h4>
                  <p>
                    You can trust Paddle to handle your IconJar license purchase
                    and delivery from start to finish, leaving you with peace of
                    mind.
                  </p>
                </Box>
                <Box mb={4}>
                  <h4>Receive frequent updates</h4>
                  <p>
                    We love hearing from our users! We actively listen to your
                    feedback to make our product even better.
                  </p>
                </Box>
                <a
                  href="https://go.setapp.com/stp177?refAppID=264&utm_medium=vendor_program&utm_source=IconJar&utm_content=link&utm_campaign=pricing_page_callout&stc=pricing_page_callout"
                  style={{ textDecoration: 'none' }}>
                  <Card p={3} animateonhover="true">
                    <Flex alignItems="center">
                      <Box width={4 / 16} style={{ minWidth: '123px' }} mr={3}>
                        <SetappLogoBanner
                          style={{
                            fill:
                              theme.appearance === 'light' ? '#31383C' : '#FFF',
                          }}
                        />
                      </Box>
                      <Box
                        width={11 / 16}
                        mr={3}
                        style={{
                          opacity: setappPriceLoaded ? 1 : 0,
                          transition: 'opacity 0.3s ease-in-out',
                        }}>
                        <p>
                          IconJar is also available on Setapp for {setappPrice}
                          /mo.
                        </p>
                      </Box>
                      <Box width={1 / 16} textAlign="right">
                        <IconChevron
                          height="12"
                          style={{
                            fill:
                              theme.appearance === 'light' ? '#748086' : '#FFF',
                          }}
                        />
                      </Box>
                    </Flex>
                  </Card>
                </a>
              </Box>
            </Flex>
          </Container>

          <Container mb={[5, 6]}>
            <Flex justifyContent="center">
              <Box width={[1, 3 / 4]}>
                <CompanyLogos />
              </Box>
            </Flex>
          </Container>

          {/* Footer */}
          <GradientWrapper pt={[5, 6]} style={{ backgroundSize: '100%' }}>
            <Container mb={[5, 6]} textAlign={'center'}>
              <Box
                style={{ width: '128px', marginTop: '-8px' }}
                mx="auto"
                mb={3}>
                <GatsbyImage
                  image={getImage(data.appIcon)}
                  alt="IconJar macOS app icon - Professional icon management tool"
                />
              </Box>
              <h2>Try IconJar Free for 14 Days</h2>
              <p>
                Experience the full power of IconJar with our free trial. No
                credit card required.
              </p>
              <TrialDownloadButton
                onClick={analyticsDownloadTrial}
                colorscheme="white"
                className="xs-hide"
                location="pricing"
              />
            </Container>
          </GradientWrapper>
        </>
      )}
    </ThemeConsumer>
  )
}

export default BuyNow

export const pageQuery = graphql`
  query {
    appIcon: file(relativePath: { eq: "app-icon@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 128
          quality: 100
          placeholder: NONE
          formats: [AUTO, WEBP]
        )
      }
    }
  }
`
